import React, { useState } from 'react';
import StatusBadge from '@/components/StatusBadge';
import { ChevronDown, ChevronUp, Bot } from 'lucide-react';

interface FooterMetric {
  label: string;
  value: string;
  color?: 'success' | 'warning' | 'danger' | 'default';
}

interface AutomationCardProps {
  id: string;
  title: string;
  owner: string;
  status: 'active' | 'running' | 'complete' | 'pending' | 'warning' | 'error' | 'idle';
  primaryMetric: string;
  primaryLabel: string;
  description: string;
  footerMetrics: FooterMetric[];
  children?: React.ReactNode;
  accentColor?: string;
}

const footerColorMap = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  default: 'text-foreground',
};

export default function AutomationCard({
  id,
  title,
  owner,
  status,
  primaryMetric,
  primaryLabel,
  description,
  footerMetrics,
  children,
}: AutomationCardProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      id={id}
      className="bg-card rounded-lg card-shadow border border-border hover:card-shadow-hover transition-all duration-200 flex flex-col"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border/60">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bot size={14} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground leading-tight truncate">{title}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{owner}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <StatusBadge variant={status} pulse={status === 'active' || status === 'running'} />
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-muted transition-colors"
            >
              {expanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
            </button>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-foreground font-tabular">{primaryMetric}</span>
          <span className="text-xs text-muted-foreground">{primaryLabel}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</p>
      </div>

      {/* Expandable body */}
      {expanded && children && (
        <div className="px-4 py-3 flex-1">
          {children}
        </div>
      )}

      {/* Footer metrics */}
      <div className="px-4 py-3 border-t border-border/60 bg-muted/20 rounded-b-lg">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {footerMetrics.map((m, i) => (
            <div key={`footer-${id}-${i}`} className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">{m.label}:</span>
              <span className={`text-xs font-semibold font-tabular ${footerColorMap[m.color ?? 'default']}`}>{m.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}